import { Button } from "@msuite/ui/button";
import { NumberInput } from "@msuite/ui/form";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Tooltip } from "@msuite/ui/tooltip";
import type { NumberFormatVariant } from "@msuite/utils/numbers";
import type { CalculationFormSchema } from "@msuite/utils/schemas";
import { Fragment } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import type { CalculationFormItemPositionFieldsSharedProps } from "./calculation-form-item-position-fields-shared";

type DimensionKey = "meters" | "length" | "width" | "height";

/** Props Interface */
interface CalculationFormItemPositionFieldsMeasurableProps
	extends CalculationFormItemPositionFieldsSharedProps {
	measurementKeys: DimensionKey[];
	variant: NumberFormatVariant;
	children: React.ReactNode;
}

export function CalculationFormItemPositionFieldsMeasurable({
	index,
	positionIndex,
	measurementKeys,
	variant,
	children,
}: CalculationFormItemPositionFieldsMeasurableProps) {
	/** Context */
	const methods = useFormContext<CalculationFormSchema>();
	const manualMeasurement = useWatch({
		control: methods.control,
		name: `services.${index}.positions.${positionIndex}.manualMeasurement`,
	});

	/** Hooks */
	const { t } = useTranslation();

	/** Computed */
	const isManual = manualMeasurement != null;

	/** Functions */
	function handleOnToggle() {
		if (isManual) {
			methods.setValue(`services.${index}.positions.${positionIndex}.manualMeasurement`, null, {
				shouldDirty: true,
			});
			return;
		}
		// Clear the dimension fields so the two modes never disagree once the position is saved.
		for (const key of measurementKeys) {
			methods.setValue(`services.${index}.positions.${positionIndex}.${key}`, null, {
				shouldDirty: true,
			});
		}
		methods.setValue(`services.${index}.positions.${positionIndex}.manualMeasurement`, 0, {
			shouldDirty: true,
		});
	}

	/** Render */
	return (
		<Fragment>
			{isManual ? (
				<NumberInput
					isRequired
					label={t("calculations.manual_measurement")}
					control={methods.control}
					name={`services.${index}.positions.${positionIndex}.manualMeasurement`}
					min={0}
					step={0.01}
					variant={variant}
					errorVariant="tooltip"
				/>
			) : (
				children
			)}
			<Tooltip
				className="self-end"
				label={
					isManual ? t("calculations.use_dimensions") : t("calculations.use_manual_measurement")
				}
			>
				<Button
					className={isManual ? "border-emerald-500" : undefined}
					icon={
						<Icon
							name="Calculator"
							className={isManual ? "stroke-emerald-500" : undefined}
						/>
					}
					variant="white"
					size="sm"
					title={
						isManual ? t("calculations.use_dimensions") : t("calculations.use_manual_measurement")
					}
					onClick={handleOnToggle}
				/>
			</Tooltip>
		</Fragment>
	);
}
